import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import Icon from './AppIcon';
import supabase from '../lib/supabaseClient';

const Analytics = () => {
  const { currentColors } = useAdminTheme();
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    newUsers: 0,
    totalResources: 0,
    totalViews: 0,
    activeSubscriptions: 0,
    revenue: 0,
    topResources: [],
    userGrowth: [],
    subscriptionBreakdown: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Get basic stats
      const { data: userStats } = await supabase
        .from('user_profiles')
        .select('id, created_at');
      
      const { data: resourceStats } = await supabase
        .from('resources')
        .select('id, view_count, created_at')
        .eq('status', 'published');
        
      const { data: subscriptionStats } = await supabase
        .from('user_subscriptions')
        .select('id, status, subscription_plans(price_monthly)')
        .eq('status', 'active');

      const { data: webinarStats } = await supabase
        .from('webinars')
        .select('id, registered_count, attended_count');

      const { data: learningPathStats } = await supabase
        .from('learning_paths')
        .select('id, enrolled_count');

      // Calculate analytics
      const now = new Date();
      const rangeStart = new Date(now.getTime() - (parseInt(timeRange) * 24 * 60 * 60 * 1000));
      
      const newUsers = userStats?.filter(u => new Date(u.created_at) > rangeStart).length || 0;
      const totalViews = resourceStats?.reduce((sum, r) => sum + (r.view_count || 0), 0) || 0;
      const revenue = subscriptionStats?.reduce((sum, s) => sum + (s.subscription_plans?.price_monthly || 0), 0) || 0;
      
      // Top resources by views
      const topResources = resourceStats
        ?.sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
        .slice(0, 5) || [];

      // User growth (simplified - last 7 days)
      const userGrowth = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
        const count = userStats?.filter(u => {
          const createdDate = new Date(u.created_at);
          return createdDate.toDateString() === date.toDateString();
        }).length || 0;
        
        userGrowth.push({
          date: date.toLocaleDateString(),
          users: count
        });
      }

      // Subscription breakdown
      const planCounts = {};
      subscriptionStats?.forEach(sub => {
        const planName = sub.subscription_plans?.price_monthly === 0 ? 'Free' :
                        sub.subscription_plans?.price_monthly === 29 ? 'Basic' :
                        sub.subscription_plans?.price_monthly === 79 ? 'Premium' : 'Enterprise';
        planCounts[planName] = (planCounts[planName] || 0) + 1;
      });

      const subscriptionBreakdown = Object.entries(planCounts).map(([plan, count]) => ({
        plan,
        count,
        percentage: Math.round((count / (subscriptionStats?.length || 1)) * 100)
      }));

      setAnalytics({
        totalUsers: userStats?.length || 0,
        newUsers,
        totalResources: resourceStats?.length || 0,
        totalViews,
        activeSubscriptions: subscriptionStats?.length || 0,
        revenue,
        topResources: await getResourceDetails(topResources),
        userGrowth,
        subscriptionBreakdown,
        totalWebinars: webinarStats?.length || 0,
        totalWebinarRegistrations: webinarStats?.reduce((sum, w) => sum + (w.registered_count || 0), 0) || 0,
        totalLearningPaths: learningPathStats?.length || 0,
        totalEnrollments: learningPathStats?.reduce((sum, l) => sum + (l.enrolled_count || 0), 0) || 0
      });
      
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getResourceDetails = async (resourceIds) => {
    if (!resourceIds.length) return [];
    
    try {
      const { data } = await supabase
        .from('resources')
        .select('id, title, view_count')
        .in('id', resourceIds.map(r => r.id));
      
      return data || [];
    } catch (error) {
      console.error('Error getting resource details:', error);
      return [];
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Icon name="Loader2" size={24} className="animate-spin" style={{ color: currentColors.primary }} />
        <span className="ml-2" style={{ color: currentColors.text }}>Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: currentColors.text }}>
            Analytics Dashboard
          </h2>
          <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
            Platform performance and user insights
          </p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
          style={{ 
            backgroundColor: currentColors.surface, 
            borderColor: currentColors.border,
            color: currentColors.text
          }}
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 3 months</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Users', value: analytics.totalUsers, icon: 'Users', change: `+${analytics.newUsers} new`, color: currentColors.primary },
          { label: 'Total Views', value: analytics.totalViews.toLocaleString(), icon: 'Eye', change: 'across all content', color: currentColors.info },
          { label: 'Active Subscriptions', value: analytics.activeSubscriptions, icon: 'Crown', change: `$${analytics.revenue}/month`, color: currentColors.success },
          { label: 'Published Resources', value: analytics.totalResources, icon: 'FileText', change: 'ready to view', color: currentColors.warning }
        ].map((metric, index) => (
          <div 
            key={index}
            className="p-6 rounded-xl border"
            style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
          >
            <div className="flex items-center justify-between mb-4">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${metric.color}20` }}
              >
                <Icon name={metric.icon} size={24} style={{ color: metric.color }} />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
                {metric.value}
              </p>
              <p className="text-sm font-medium" style={{ color: currentColors.textSecondary }}>
                {metric.label}
              </p>
              <p className="text-xs mt-1" style={{ color: metric.color }}>
                {metric.change}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Resources */}
        <div 
          className="p-6 rounded-xl border"
          style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
        >
          <h3 className="text-lg font-bold mb-4" style={{ color: currentColors.text }}>
            Top Resources by Views
          </h3>
          <div className="space-y-3">
            {analytics.topResources.map((resource, index) => (
              <div key={resource.id} className="flex items-center justify-between p-3 rounded-lg"
                   style={{ backgroundColor: currentColors.background }}>
                <div className="flex items-center">
                  <span 
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3"
                    style={{ backgroundColor: currentColors.primary, color: 'white' }}
                  >
                    {index + 1}
                  </span>
                  <div>
                    <p className="font-medium" style={{ color: currentColors.text }}>
                      {resource.title}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold" style={{ color: currentColors.primary }}>
                    {resource.view_count || 0}
                  </p>
                  <p className="text-xs" style={{ color: currentColors.textMuted }}>
                    views
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Subscription Breakdown */}
        <div 
          className="p-6 rounded-xl border"
          style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
        >
          <h3 className="text-lg font-bold mb-4" style={{ color: currentColors.text }}>
            Subscription Plans
          </h3>
          <div className="space-y-4">
            {analytics.subscriptionBreakdown.map((sub, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-full mr-3"
                    style={{ 
                      backgroundColor: index === 0 ? currentColors.primary : 
                                     index === 1 ? currentColors.success : 
                                     index === 2 ? currentColors.warning : currentColors.info 
                    }}
                  />
                  <span style={{ color: currentColors.text }}>{sub.plan}</span>
                </div>
                <div className="text-right">
                  <span className="font-bold" style={{ color: currentColors.text }}>
                    {sub.count}
                  </span>
                  <span className="text-sm ml-2" style={{ color: currentColors.textMuted }}>
                    ({sub.percentage}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* User Growth */}
        <div 
          className="p-6 rounded-xl border"
          style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
        >
          <h3 className="text-lg font-bold mb-4" style={{ color: currentColors.text }}>
            User Growth (Last 7 Days)
          </h3>
          <div className="space-y-2">
            {analytics.userGrowth.map((day, index) => (
              <div key={index} className="flex items-center justify-between p-2">
                <span className="text-sm" style={{ color: currentColors.textSecondary }}>
                  {day.date}
                </span>
                <div className="flex items-center">
                  <div 
                    className="h-2 rounded-full mr-3"
                    style={{ 
                      backgroundColor: currentColors.primary,
                      width: `${Math.max(day.users * 20, 4)}px`
                    }}
                  />
                  <span className="font-medium" style={{ color: currentColors.text }}>
                    {day.users}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Stats */}
        <div 
          className="p-6 rounded-xl border"
          style={{ backgroundColor: currentColors.surface, borderColor: currentColors.border }}
        >
          <h3 className="text-lg font-bold mb-4" style={{ color: currentColors.text }}>
            Content Engagement
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span style={{ color: currentColors.textSecondary }}>Total Webinars</span>
              <span className="font-bold" style={{ color: currentColors.text }}>
                {analytics.totalWebinars}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: currentColors.textSecondary }}>Webinar Registrations</span>
              <span className="font-bold" style={{ color: currentColors.text }}>
                {analytics.totalWebinarRegistrations}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: currentColors.textSecondary }}>Learning Paths</span>
              <span className="font-bold" style={{ color: currentColors.text }}>
                {analytics.totalLearningPaths}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: currentColors.textSecondary }}>Total Enrollments</span>
              <span className="font-bold" style={{ color: currentColors.text }}>
                {analytics.totalEnrollments}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
